import { Temporal } from "temporal-polyfill";

import type {
  Bar,
  DatafeedConfiguration,
  IBasicDataFeed,
  LibrarySymbolInfo,
  ResolutionString,
} from "~/chart";
import instance from "~/v2/api";
import type { PortfolioHistoryChartPoint } from "./portfolioHistory";
import { PORTFOLIO_TIMEFRAMES } from "./portfolioHistoryTimeSettings";

export const PORTFOLIO_SYMBOL = "ACCOUNT_VALUE";
const supportedResolutions = Object.keys(
  PORTFOLIO_TIMEFRAMES,
) as ResolutionString[];

const configuration: DatafeedConfiguration = {
  supported_resolutions: supportedResolutions,
  supports_marks: false,
  supports_timescale_marks: false,
  supports_time: false,
};

const symbolInfo: LibrarySymbolInfo = {
  name: PORTFOLIO_SYMBOL,
  ticker: PORTFOLIO_SYMBOL,
  description: "Account value",
  type: "portfolio",
  session: "24x7",
  exchange: "Alpaca",
  listed_exchange: "Alpaca",
  timezone: "Etc/UTC",
  format: "price",
  pricescale: 100,
  minmov: 1,
  has_intraday: true,
  intraday_multipliers: ["1", "5", "15", "60"],
  has_daily: true,
  supported_resolutions: supportedResolutions,
  visible_plots_set: "c",
  data_status: "endofday",
};

const overlayTimeframes: Record<string, string> = {
  "1": "1Min",
  "5": "5Min",
  "15": "15Min",
  "60": "1Hour",
  "1D": "1Day",
};

const POLLING_INTERVAL_MS = 10_000;
const DELAYED_MARKET_DATA_MS = 16 * 60_000;
const MARKET_TIMEZONE = "America/New_York";

const pollLookbackSeconds: Record<string, number> = {
  "1": 3 * 60,
  "5": 15 * 60,
  "15": 45 * 60,
  "60": 3 * 60 * 60,
  "1D": 3 * 24 * 60 * 60,
};

const PORTFOLIO_HISTORY_END_LAG_SECONDS = 60;

const isCryptoSymbol = (symbol: string) => symbol.includes("/");

const formatStockHistoryEndDate = (
  periodToMs: number,
  resolution: string,
  isSubscribed: boolean,
): string => {
  const cappedToMs = Math.min(periodToMs, Date.now());

  if (resolution === "1D") {
    return Temporal.Instant.fromEpochMilliseconds(cappedToMs)
      .toZonedDateTimeISO("UTC")
      .subtract({ days: 1 })
      .toPlainDate()
      .toZonedDateTime({
        timeZone: MARKET_TIMEZONE,
        plainTime: Temporal.PlainTime.from("00:00"),
      })
      .toString({ timeZoneName: "never" });
  }

  return new Date(
    cappedToMs - (isSubscribed ? 0 : DELAYED_MARKET_DATA_MS),
  ).toISOString();
};

const createOverlaySymbolInfo = (symbol: string): LibrarySymbolInfo => {
  const crypto = isCryptoSymbol(symbol);
  return {
    name: symbol,
    ticker: symbol,
    description: symbol,
    type: crypto ? "crypto" : "stock",
    session: crypto ? "24x7" : "0400-2000",
    exchange: crypto ? "Alpaca Crypto" : "NYSE",
    listed_exchange: crypto ? "Alpaca Crypto" : "NYSE",
    timezone: crypto ? "Etc/UTC" : "America/New_York",
    format: "price",
    pricescale: 100,
    minmov: 1,
    has_intraday: true,
    intraday_multipliers: ["1", "5", "15", "60"],
    has_daily: true,
    supported_resolutions: supportedResolutions,
    volume_precision: 8,
    data_status: "streaming",
  };
};

const toOverlayBars = (value: unknown): Bar[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((bar): Bar[] => {
    if (!bar || typeof bar !== "object") {
      return [];
    }
    if (
      !("t" in bar) ||
      typeof bar.t !== "string" ||
      !("o" in bar) ||
      typeof bar.o !== "number" ||
      !("h" in bar) ||
      typeof bar.h !== "number" ||
      !("l" in bar) ||
      typeof bar.l !== "number" ||
      !("c" in bar) ||
      typeof bar.c !== "number"
    ) {
      return [];
    }
    return [
      {
        time: Date.parse(bar.t),
        open: bar.o,
        high: bar.h,
        low: bar.l,
        close: bar.c,
        volume: "v" in bar && typeof bar.v === "number" ? bar.v : undefined,
      },
    ];
  });
};

const getObjectValue = (value: object, key: string): unknown =>
  Object.entries(value).find(([entryKey]) => entryKey === key)?.[1];

const getResponseBars = (data: unknown, symbol: string): Bar[] => {
  if (!data || typeof data !== "object" || !("bars" in data)) {
    return [];
  }
  const bars = data.bars;
  if (isCryptoSymbol(symbol)) {
    if (!bars || typeof bars !== "object" || !(symbol in bars)) {
      return [];
    }
    return toOverlayBars(getObjectValue(bars, symbol));
  }
  return toOverlayBars(bars);
};

const getOverlayBarsUrl = ({
  symbol,
  from,
  to,
  timeframe,
  limit,
  latest,
}: {
  symbol: string;
  from: string;
  to: string;
  timeframe: string;
  limit: number;
  latest?: boolean;
}) => {
  const suffix = latest ? "&sort=desc" : "";
  return isCryptoSymbol(symbol)
    ? `${window.env.CRYPTO_DATA_API_URL}/bars?symbols=${symbol}&start=${from}&end=${to}&timeframe=${timeframe}&limit=${limit}${suffix}`
    : `${window.env.DATA_API_URL}/stocks/${symbol}/bars?start=${from}&end=${to}&timeframe=${timeframe}&adjustment=split&limit=${limit}${suffix}`;
};

export type OverlayBarsRequest = Parameters<typeof getOverlayBarsUrl>[0];

export interface PortfolioComparisonHistoryRequest {
  from: number;
  isStockDataSubscribed: boolean;
  resolution: ResolutionString;
  symbol: string;
  to: number;
}

const getNextPageToken = (response: unknown): string | undefined => {
  if (
    typeof response !== "object" ||
    response === null ||
    !("next_page_token" in response) ||
    typeof response.next_page_token !== "string"
  ) {
    return undefined;
  }
  return response.next_page_token;
};

export const getPortfolioComparisonHistory = async ({
  from,
  isStockDataSubscribed,
  resolution,
  symbol: requestedSymbol,
  to,
}: PortfolioComparisonHistoryRequest): Promise<
  PortfolioHistoryChartPoint[]
> => {
  const symbol = requestedSymbol.trim().toUpperCase();
  const timeframe = overlayTimeframes[resolution];
  if (!symbol || !timeframe) {
    throw new Error(`Unsupported comparison request: ${requestedSymbol}`);
  }

  const end = isCryptoSymbol(symbol)
    ? new Date(to * 1_000).toISOString()
    : formatStockHistoryEndDate(to * 1_000, resolution, isStockDataSubscribed);
  const url = getOverlayBarsUrl({
    symbol,
    from: new Date(from * 1_000).toISOString(),
    to: end,
    timeframe,
    limit: 10_000,
  });
  const bars: Bar[] = [];
  const seenPageTokens = new Set<string>();
  let pageToken: string | undefined;
  do {
    const pageUrl = pageToken
      ? `${url}&page_token=${encodeURIComponent(pageToken)}`
      : url;
    const { data } = await instance.get<unknown>(pageUrl);
    bars.push(...getResponseBars(data, symbol));
    pageToken = getNextPageToken(data);
    if (pageToken && seenPageTokens.has(pageToken)) {
      break;
    }
    if (pageToken) {
      seenPageTokens.add(pageToken);
    }
  } while (pageToken);

  return bars
    .sort((left, right) => left.time - right.time)
    .map(({ time, close }) => ({
      time: Math.floor(time / 1_000),
      value: close,
    }))
    .filter(({ time }) => time >= from && time <= to);
};

export interface PortfolioHistoryDatafeedOptions {
  requestOverlayBars?: (
    request: OverlayBarsRequest,
  ) => Promise<{ data: unknown }>;
}

export const toPortfolioHistoryBars = (
  data: readonly PortfolioHistoryChartPoint[],
): Bar[] =>
  data.map(({ time, value }) => ({
    time: time * 1_000,
    open: value,
    high: value,
    low: value,
    close: value,
  }));

export interface PortfolioHistoryRangeRequest {
  resolution: ResolutionString;
  timeframe: (typeof PORTFOLIO_TIMEFRAMES)[string];
  from: number;
  to: number;
}

export type LoadPortfolioHistoryRange = (
  request: PortfolioHistoryRangeRequest,
) => Promise<readonly PortfolioHistoryChartPoint[]>;

export interface PortfolioHistoryDatafeed extends IBasicDataFeed {
  dispose: () => void;
  updatePortfolioData: (data: readonly PortfolioHistoryChartPoint[]) => void;
}

const mergePoints = (
  current: readonly PortfolioHistoryChartPoint[],
  incoming: readonly PortfolioHistoryChartPoint[],
) => {
  const points = new Map(current.map((point) => [point.time, point]));
  incoming.forEach((point) => {
    points.set(point.time, point);
  });
  return Array.from(points.values()).sort(
    (left, right) => left.time - right.time,
  );
};

export const createPortfolioHistoryDatafeed = (
  data: readonly PortfolioHistoryChartPoint[],
  initialResolution: ResolutionString = "1" as ResolutionString,
  loadRange?: LoadPortfolioHistoryRange,
  getIsStockDataSubscribed: () => boolean = () => false,
  options: PortfolioHistoryDatafeedOptions = {},
): PortfolioHistoryDatafeed => {
  const pointsByResolution = new Map<
    ResolutionString,
    PortfolioHistoryChartPoint[]
  >([[initialResolution, [...data]]]);
  const loadedRanges = new Map<
    ResolutionString,
    { from: number; to: number }
  >();
  if (data.length > 0) {
    loadedRanges.set(initialResolution, {
      from: data[0].time,
      to: data.at(-1)?.time ?? data[0].time,
    });
  }
  const pendingLoads = new Map<string, Promise<void>>();
  const pollIntervals = new Map<string, ReturnType<typeof setInterval>>();
  const inFlightPolls = new Map<string, symbol>();
  const lastPollStartedAt = new Map<string, number>();
  const lastBarTimes = new Map<string, number>();
  const subscriptions = new Map<
    string,
    {
      symbol: string;
      resolution: ResolutionString;
      onTick: (bar: Bar) => void;
    }
  >();

  const requestOverlayBars = (request: OverlayBarsRequest) =>
    options.requestOverlayBars?.(request) ??
    instance.get<unknown>(getOverlayBarsUrl(request));

  const ensureRange = async (
    resolution: ResolutionString,
    from: number,
    to: number,
  ) => {
    const safeTo = Math.min(
      to,
      Math.floor(Date.now() / 1_000) - PORTFOLIO_HISTORY_END_LAG_SECONDS,
    );
    const timeframe = PORTFOLIO_TIMEFRAMES[resolution];
    const loaded = loadedRanges.get(resolution);
    if (
      !loadRange ||
      !timeframe ||
      safeTo <= from ||
      (loaded && loaded.from <= from && loaded.to >= safeTo)
    ) {
      return;
    }

    const key = `${resolution}:${from}:${safeTo}`;
    let pending = pendingLoads.get(key);
    if (!pending) {
      pending = loadRange({ resolution, timeframe, from, to: safeTo }).then(
        (incoming) => {
          pointsByResolution.set(
            resolution,
            mergePoints(pointsByResolution.get(resolution) ?? [], incoming),
          );
          loadedRanges.set(resolution, {
            from: Math.min(from, loaded?.from ?? from),
            to: Math.max(safeTo, loaded?.to ?? safeTo),
          });
        },
      );
      pendingLoads.set(key, pending);
      void pending.then(
        () => pendingLoads.delete(key),
        () => pendingLoads.delete(key),
      );
    }
    await pending;
  };

  return {
    dispose: () => {
      pollIntervals.forEach(clearInterval);
      pollIntervals.clear();
      subscriptions.clear();
      lastPollStartedAt.clear();
      lastBarTimes.clear();
    },
    updatePortfolioData: (nextData) => {
      pointsByResolution.set(initialResolution, [...nextData]);
      if (nextData.length === 0) {
        loadedRanges.delete(initialResolution);
        return;
      }
      loadedRanges.set(initialResolution, {
        from: nextData[0].time,
        to: nextData.at(-1)?.time ?? nextData[0].time,
      });
    },
    onReady: (callback) => setTimeout(() => callback(configuration), 0),
    searchSymbols: (input, _exchange, _type, callback) => {
      const symbol = input.trim().toUpperCase();
      callback(
        symbol
          ? [
              {
                symbol,
                ticker: symbol,
                description: symbol,
                exchange: isCryptoSymbol(symbol) ? "Crypto" : "",
                type: isCryptoSymbol(symbol) ? "crypto" : "stock",
              },
            ]
          : [],
      );
    },
    resolveSymbol: (name, onResolve) =>
      setTimeout(
        () =>
          onResolve(
            name === PORTFOLIO_SYMBOL
              ? { ...symbolInfo }
              : createOverlaySymbolInfo(name),
          ),
        0,
      ),
    getBars: (info, resolution, period, onResult, onError) => {
      const requestedSymbol = info.name || info.ticker;
      if (requestedSymbol && requestedSymbol !== PORTFOLIO_SYMBOL) {
        const timeframe = overlayTimeframes[resolution];
        if (!timeframe) {
          onError(`Unsupported overlay resolution: ${resolution}`);
          return;
        }
        const crypto = isCryptoSymbol(requestedSymbol);
        const to = crypto
          ? new Date(period.to * 1_000).toISOString()
          : formatStockHistoryEndDate(
              period.to * 1_000,
              resolution,
              getIsStockDataSubscribed(),
            );
        requestOverlayBars({
          symbol: requestedSymbol,
          from: new Date(period.from * 1_000).toISOString(),
          to,
          timeframe,
          limit: 10_000,
        })
          .then(({ data }) => {
            const bars = getResponseBars(data, requestedSymbol).sort(
              (left, right) => left.time - right.time,
            );
            onResult(bars, { noData: bars.length === 0 });
          })
          .catch(() => onError(`Failed to load bars for ${requestedSymbol}`));
        return;
      }
      void ensureRange(resolution, period.from, period.to)
        .then(() => {
          const bars = toPortfolioHistoryBars(
            pointsByResolution.get(resolution) ?? [],
          );
          const toIndex = bars.findIndex(
            (bar) => bar.time >= period.to * 1_000,
          );
          const end = toIndex === -1 ? bars.length : toIndex;
          const fromIndex = bars.findIndex(
            (bar) => bar.time >= period.from * 1_000,
          );
          const requestedStart = fromIndex === -1 ? end : fromIndex;
          const start = Math.max(
            0,
            Math.min(requestedStart, end - period.countBack),
          );
          const result = bars.slice(start, end);

          onResult(result, { noData: result.length === 0 });
        })
        .catch((error: unknown) =>
          onError(
            error instanceof Error
              ? error.message
              : "Portfolio history request failed",
          ),
        );
    },
    subscribeBars: (info, resolution, onTick, listenerGuid) => {
      const requestedSymbol = (info.name || info.ticker)?.toUpperCase();
      if (!requestedSymbol) {
        return;
      }
      const timeframe = overlayTimeframes[resolution];
      const lookbackSeconds = pollLookbackSeconds[resolution];
      if (!timeframe || !lookbackSeconds) {
        return;
      }
      subscriptions.set(listenerGuid, {
        symbol: requestedSymbol,
        resolution,
        onTick,
      });
      const poll = () => {
        const now = Date.now();
        const lastStartedAt = lastPollStartedAt.get(requestedSymbol);
        if (
          inFlightPolls.has(requestedSymbol) ||
          (lastStartedAt !== undefined &&
            now - lastStartedAt < POLLING_INTERVAL_MS)
        ) {
          return;
        }
        const pollToken = Symbol(requestedSymbol);
        inFlightPolls.set(requestedSymbol, pollToken);
        lastPollStartedAt.set(requestedSymbol, now);
        const to = Math.floor(now / 1_000);
        const request =
          requestedSymbol === PORTFOLIO_SYMBOL
            ? ensureRange(resolution, to - lookbackSeconds, to).then(() => {
                const points = pointsByResolution.get(resolution) ?? [];
                const latest = points.at(-1);
                return latest ? toPortfolioHistoryBars([latest])[0] : undefined;
              })
            : requestOverlayBars({
                symbol: requestedSymbol,
                from: new Date((to - lookbackSeconds) * 1_000).toISOString(),
                to: isCryptoSymbol(requestedSymbol)
                  ? new Date(to * 1_000).toISOString()
                  : formatStockHistoryEndDate(
                      to * 1_000,
                      resolution,
                      getIsStockDataSubscribed(),
                    ),
                timeframe,
                limit: 1,
                latest: true,
              }).then(
                ({ data }) =>
                  getResponseBars(data, requestedSymbol).sort(
                    (left, right) => right.time - left.time,
                  )[0],
              );
        void request
          .then((bar) => {
            const barKey = `${requestedSymbol}:${resolution}`;
            const lastBarTime = lastBarTimes.get(barKey);
            if (bar && (lastBarTime === undefined || bar.time >= lastBarTime)) {
              lastBarTimes.set(barKey, bar.time);
              subscriptions.forEach((subscription) => {
                if (
                  subscription.symbol === requestedSymbol &&
                  subscription.resolution === resolution
                ) {
                  subscription.onTick(bar);
                }
              });
            }
          })
          .catch(() => undefined)
          .finally(() => {
            if (inFlightPolls.get(requestedSymbol) === pollToken) {
              inFlightPolls.delete(requestedSymbol);
            }
          });
      };
      const existing = pollIntervals.get(listenerGuid);
      if (existing) {
        clearInterval(existing);
      }
      pollIntervals.set(listenerGuid, setInterval(poll, POLLING_INTERVAL_MS));
      poll();
    },
    unsubscribeBars: (listenerGuid) => {
      subscriptions.delete(listenerGuid);
      const interval = pollIntervals.get(listenerGuid);
      if (interval) {
        clearInterval(interval);
        pollIntervals.delete(listenerGuid);
      }
    },
  };
};
